/**
 * Pure inventory valuation logic (CC-6: NEVER use Lingui macros here).
 *
 * AVCO method (Average Cost / moving average cost): each inbound (receipt /
 * purchase) recalculates the average unit cost of the product; each outbound is
 * valued at the current average cost. Valuation layers (valuation_layer)
 * are append-only and auditable: one per movement with value impact.
 *
 *   - Inbound  (qty > 0): unit_cost = purchase cost; value = qty × unit_cost.
 *                         The moving average is recalculated on the accumulated balance.
 *   - Outbound (qty < 0): unit_cost = current moving average; value = qty × avg
 *                         (negative, deducts value from inventory).
 *
 * EVERYTHING in INTEGERS (minor units, scale factor 1000), same as F1/F2:
 *   - QUANTITIES are scaled ×1000 (QTY_SCALE).
 *   - UNIT COSTS are in minor units of currency (e.g. cents)
 *     but expressed PER WHOLE UNIT of product (not per thousandth).
 *   - value = round(qty_minor × unit_cost / QTY_SCALE) → minor units of currency.
 *
 * This avoids dragging the 1000 factor into the unit cost and keeps
 * `value` on the same monetary scale as `unit_cost`.
 */

import { QTY_SCALE } from "./units";

/** Accumulated valuation state of a product (quantity and value balance). */
export interface AvcoState {
  /** Accumulated on-hand quantity (minor units, ×1000). */
  qty: number;
  /** Total accumulated inventory value (minor units of currency). */
  value: number;
}

/** Initial empty state. */
export const EMPTY_AVCO_STATE: AvcoState = { qty: 0, value: 0 };

/**
 * Calculates value (minor units of currency) for a given quantity at a unit
 * cost per whole unit. `qtyMinor` is scaled ×1000.
 */
export function valueOf(qtyMinor: number, unitCost: number): number {
  return Math.round((qtyMinor * unitCost) / QTY_SCALE);
}

/**
 * Current moving average unit cost (per whole unit) given accumulated state.
 * Returns 0 if there is no balance (avoids division by zero).
 */
export function averageUnitCost(state: AvcoState): number {
  if (state.qty <= 0) {
    return 0;
  }
  return Math.round((state.value * QTY_SCALE) / state.qty);
}

/** Result of applying a valuation movement. */
export interface AvcoLayer {
  /** Quantity delta (minor units, ×1000). >0 inbound, <0 outbound. */
  qty: number;
  /** Unit cost (per whole unit) assigned to this layer. */
  unitCost: number;
  /** Layer value (minor units of currency). Sign follows qty. */
  value: number;
  /** Accumulated state after applying the layer. */
  state: AvcoState;
}

/**
 * Applies an INBOUND (receipt/purchase) to AVCO state. `qtyMinor` > 0 (×1000),
 * `purchaseUnitCost` is the purchase cost per whole unit. Recalculates the
 * moving average by summing quantity and value.
 */
export function applyInbound(
  state: AvcoState,
  qtyMinor: number,
  purchaseUnitCost: number,
): AvcoLayer {
  const layerValue = valueOf(qtyMinor, purchaseUnitCost);
  const next: AvcoState = {
    qty: state.qty + qtyMinor,
    value: state.value + layerValue,
  };
  return { qty: qtyMinor, unitCost: purchaseUnitCost, value: layerValue, state: next };
}

/**
 * Applies an OUTBOUND (delivery/consumption) to AVCO state. `qtyMinor` > 0 (×1000):
 * the resulting layer is negative. The outbound is valued at the CURRENT moving average
 * (before deduction). Returns negative qty/value.
 */
export function applyOutbound(state: AvcoState, qtyMinor: number): AvcoLayer {
  const avg = averageUnitCost(state);
  const layerValue = valueOf(qtyMinor, avg);
  const next: AvcoState = {
    qty: state.qty - qtyMinor,
    value: state.value - layerValue,
  };
  // `|| 0` avoids -0 when layerValue/qtyMinor are 0 (Object.is(-0,0) === false).
  return { qty: -qtyMinor || 0, unitCost: avg, value: -layerValue || 0, state: next };
}

/** An already persisted valuation layer (relevant subset for reduction). */
export interface ValuationLayerLike {
  product_id: string | null;
  qty: number | null;
  value: number | null;
}

/**
 * Reduces a product's layers to its accumulated AVCO state (quantity and
 * value balance). Layers must come in chronological creation order.
 */
export function reduceLayers(layers: ValuationLayerLike[]): AvcoState {
  let qty = 0;
  let value = 0;
  for (const layer of layers) {
    qty += layer.qty ?? 0;
    value += layer.value ?? 0;
  }
  return { qty, value };
}

/** On-hand inventory value per product, derived from layers. */
export interface InventoryValueRow {
  productId: string;
  /** Accumulated on-hand (minor units, ×1000). */
  onHand: number;
  /** Total inventory value (minor units of currency). */
  value: number;
  /** Current moving average unit cost (per whole unit). */
  avgUnitCost: number;
}

/**
 * Aggregates valuation layers by product and returns each product's on-hand
 * inventory value (on-hand, total value, and average cost). Simple valuation
 * report (CC-6: pure calculation, no Lingui).
 */
export function computeInventoryValue(layers: ValuationLayerLike[]): InventoryValueRow[] {
  const byProduct = new Map<string, AvcoState>();
  for (const layer of layers) {
    const pid = layer.product_id;
    if (!pid) {
      continue;
    }
    const cur = byProduct.get(pid) ?? { qty: 0, value: 0 };
    cur.qty += layer.qty ?? 0;
    cur.value += layer.value ?? 0;
    byProduct.set(pid, cur);
  }
  const rows: InventoryValueRow[] = [];
  for (const [productId, state] of byProduct) {
    rows.push({
      productId,
      onHand: state.qty,
      value: state.value,
      avgUnitCost: averageUnitCost(state),
    });
  }
  return rows;
}
